import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, MenuItem, Tooltip, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {AddOutlined, DeleteOutline, Edit, OpenInNew} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import useSearch from "../../../../../hooks/use-search";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {useBizFormApiResolver} from "../../../../../hooks/use-biz-form-api-resolver";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import IconButton from "../../../../../components/base/button/icon_button";
import Button from "../../../../../components/base/button/button";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import Form, {MuiRFF, Yup} from "../../../../../components/form";
import useQuery from "../../../../../hooks/use-query";
import {FormResponseStatuses} from "../../../../../../packages/biz-form/core/constants/form-response-statuses";

const dataGridSaveConfig = {
    name: "FormsManagementListDataGrid",
    version: 2,
};

export const FormPublicFilter = {
    all: "all",
    public: "public",
    private: "private",
};

const dataGridColumnNames = {
    title: "title",
    description: "description",
    isPublic: "isPublic",
    responseCount: "responseCount",
    pending: "pending",
    declined: "declined",
    approved: "approved",
    createdByName: "createdByName",
    actions: "actions",
};

const getStatusCount = (item, statusName) =>
    item.responseCountByStatus?.find((s) => s.name === statusName)?.count ?? 0;

const resolveIsPublicFilter = (filters) => {
    switch (filters?.isPublic) {
        case FormPublicFilter.public:
            return true;
        case FormPublicFilter.private:
            return false;
        default:
            return undefined;
    }
};

const FormsListView = () => {
    const {t} = useTranslation("views", {keyPrefix: "forms-management.list"});
    const [, setQuery] = useQuery();
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const api = useBizFormApiResolver((t) => t.operatorApp);
    const operatorAccessLevels = useAccessLevels();
    const {configureNotebookTags} = useConfigureNotebook("forms-management-list");
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [data, setData] = useState([]);
    const [creating, setCreating] = useState(false);

    /**
     * Configures notebook tags and behavioral analytics tool for the forms management list view.
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.formsManagementList);
        configureBehavioralTool((tags) => tags.formsManagementList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(
        () => [
            {
                title: t("columns.title"),
                name: dataGridColumnNames.title,
                type: DataGridColumnTypes.string,
                alignment: DataGridColumnAlignments.left,
            },
            {
                title: t("columns.description"),
                name: dataGridColumnNames.description,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                sortable: false,
                width: 200,
            },
            {
                title: t("columns.isPublic"),
                name: dataGridColumnNames.isPublic,
                type: DataGridColumnTypes.boolean,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                width: 90,
            },
            {
                title: t("columns.responseCount"),
                name: dataGridColumnNames.responseCount,
                type: DataGridColumnTypes.string,
                alignment: DataGridColumnAlignments.center,
                width: 100,
            },
            {
                title: t("columns.pending"),
                name: dataGridColumnNames.pending,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                width: 100,
            },
            {
                title: t("columns.declined"),
                name: dataGridColumnNames.declined,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                width: 100,
            },
            {
                title: t("columns.approved"),
                name: dataGridColumnNames.approved,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                width: 100,
            },
            {
                title: t("columns.createdByName"),
                name: dataGridColumnNames.createdByName,
                type: DataGridColumnTypes.string,
                alignment: DataGridColumnAlignments.left,
                width: 150,
            },
            {
                name: dataGridColumnNames.actions,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                pinned: true,
                pinnedToggleable: false,
                visibilityToggleable: false,
                width: operatorAccessLevels.packages.bizForm.formsManage ? 90 : 0,
            },
        ], [operatorAccessLevels.packages.bizForm.formsManage, t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo), [dataGridPageSize]);

    /**
     * Performs the search for forms based on the given arguments, updates the data state with the results, and handles pagination info.
     * @type {(function(*): Promise<void>)|*}
     */
    const search = useCallback(async (args) => {
        const isPublic = resolveIsPublicFilter(args.filters);
        const forApi = {
            keyword: args.filters?.keyword ?? "",
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{property: args.orderBy.field, isDesc: args.orderBy.descending}] : [],
            ...(isPublic !== undefined && {isPublic}),
        };
        const response = await api.listForms(forApi);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag && response.data) {
            setData(response.data.items ?? []);
            args.setPaginationInfo((prev) => ({
                ...prev,
                length: response.data.totalCount ?? 0,
            }));
        }
    }, [api, isMounted]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: "", isPublic: FormPublicFilter.all},
    }), [dataGridSortBy, initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings, true);
    const {performSearch} = searchData;

    /**
     * Opens the remove confirmation dialog with the given options.
     * @type {(function({}=): void)|*}
     */
    const openRemoveConfirmation = useCallback((options = {}) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {open: true, ...options}));
    }, [dispatch]);

    /**
     * Handles the deletion of a form by opening a confirmation dialog and, if confirmed, calling the API to delete the form and refreshing the list.
     * @type {(function(*): void)|*}
     */
    const onDelete = useCallback((row) => {
        openRemoveConfirmation({
            data: null,
            removedEntryTitle: t("deleteFormConfirmation", {title: row.title}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return false;
                }
                const del = await api.deleteForm({id: row.id});
                if (del?.resultFlag) {
                    await performSearch();
                }
                return del?.resultFlag ?? false;
            },
        });
    }, [api, openRemoveConfirmation, performSearch, t]);

    /**
     * Handles the editing of a form by navigating to the edit page for the selected form.
     * @type {(function(*): void)|*}
     */
    const onEdit = useCallback((formId) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.editBizForm, {
            open: true,
            formReference: formId,
            formApiResolver: (t) => t.operatorApp,
            onCloseCallback: () => {
                void searchData.performSearch();
            },
        }));
    }, [dispatch, searchData]);

    /**
     * Creates a new form and opens the edit dialog for the operator to configure it.
     * @type {(function(): Promise<void>)|*}
     */
    const onCreateForm = useCallback(async () => {
        setCreating(true);
        const response = await api.createForm({
            title: t("defaultFormTitle"),
            isPublic: false,
        });
        if (!isMounted()) {
            return;
        }
        setCreating(false);
        if (response?.resultFlag && response.data?.id != null) {
            onEdit(response.data.id);
        }
    }, [api, isMounted, onEdit, t]);

    /**
     * Handles viewing form responses by navigating to the responses page with filters set for the selected form.
     * @type {(function(*): void)|*}
     */
    const viewResponses = useCallback((form) => {
        setQuery({form: JSON.stringify(form)}, {location: {pathname: routes.forms.responses}, replace: false});
    }, [setQuery]);

    const renderStatusCell = useCallback((count, color) => (
        <Box sx={{display: "flex", alignItems: "center", gap: 1, justifyContent: "center"}}>
            <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: color}}/>
            {count}
        </Box>
    ), []);

    const rows = useMemo(() =>
        data.map((e) => ({
            key: e.id,
            data: e,
            cells: {
                [dataGridColumnNames.title]: e.title,
                [dataGridColumnNames.description]:
                    <Tooltip title={e.description ?? ""} placement="bottom" arrow disableHoverListener={!e.description}>
                        <Typography {...dataGridCellTextProps} variant="body2">
                            {e.description ?? ""}
                        </Typography>
                    </Tooltip>,
                [dataGridColumnNames.isPublic]: e.isPublic,
                [dataGridColumnNames.responseCount]: e.responseCount ?? 0,
                [dataGridColumnNames.pending]: renderStatusCell(
                    getStatusCount(e, FormResponseStatuses.Pending),
                    "warning.main",
                ),
                [dataGridColumnNames.declined]: renderStatusCell(
                    getStatusCount(e, FormResponseStatuses.Declined),
                    "error.main",
                ),
                [dataGridColumnNames.approved]: renderStatusCell(
                    getStatusCount(e, FormResponseStatuses.Approved),
                    "success.main",
                ),
                [dataGridColumnNames.createdByName]: e.createdByName ?? "",
                [dataGridColumnNames.actions]:
                    <Box sx={{display: "flex", gap: 0.5, justifyContent: "center"}}>
                        {
                            operatorAccessLevels.packages.bizForm.formsManage &&
                            <>
                                <Tooltip title={t("actions.deleteForm")}>
                                    <IconButton color="paper.error" size="small" onPressed={() => onDelete(e)} aria-label={t("actions.deleteForm")}>
                                        <DeleteOutline className="icon small"/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t("actions.editForm")}>
                                    <IconButton color="paper.secondary" size="small" onPressed={() => onEdit(e.id)} aria-label={t("actions.editForm")}>
                                        <Edit className="icon small"/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        }
                        <Tooltip title={t("actions.viewResponses")}>
                            <IconButton color="paper.primary" size="small" onPressed={() => viewResponses(e)} aria-label={t("actions.viewResponses")}>
                                <OpenInNew className="icon small"/>
                            </IconButton>
                        </Tooltip>
                    </Box>,
            },
        })), [data, onDelete, onEdit, operatorAccessLevels.packages.bizForm.formsManage, renderStatusCell, t, viewResponses]);

    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%", gap: 4}}>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", my: 2, width: "100%"}}>
                <Typography variant="h6" fontWeight={600}>
                    {t("pageTitle")}
                </Typography>
                {
                    operatorAccessLevels.packages.bizForm.formsManage &&
                    <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        disabled={creating}
                        onClick={onCreateForm}
                        startIcon={<AddOutlined/>}
                    >
                        {creating ? t("creatingForm") : t("createForm")}
                    </Button>
                }
            </Box>
            <FormListViewSearchSection
                filters={searchData.filters}
                setFilters={searchData.setFilters}
            />
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                hideExporting
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) =>
                    searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))
                }
                onPageSizeChanged={(_, current) =>
                    searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))
                }
            />
        </Box>
    );
};

export default FormsListView;


const FormListViewSearchSection = ({filters, setFilters}) => {
    const {t} = useTranslation("views", {keyPrefix: "forms-management.list.search"});

    const schema = useMemo(() => Yup.object().shape({
        keyword: Yup.string().optional().nullable(),
        isPublic: Yup.string().optional().nullable(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        keyword: filters.keyword ?? "",
        isPublic: filters.isPublic ?? FormPublicFilter.all,
    }), [filters]);

    const isPublicOptions = useMemo(() => [
        {value: FormPublicFilter.all, label: t("isPublicAll")},
        {value: FormPublicFilter.public, label: t("isPublicPublic")},
        {value: FormPublicFilter.private, label: t("isPublicPrivate")},
    ], [t]);

    /**
     * Handles form submission by updating the filters state with the form values.
     * @type {(function(*): void)|*}
     */
    const submitForm = useCallback((values) => {
        setFilters((prev) => ({
            ...prev,
            keyword: values.keyword,
            isPublic: values.isPublic,
        }));
    }, [setFilters]);

    return (
        <Form
            initialValues={initialValues}
            validate={validate}
            onSubmit={submitForm}
            formProps={{className: "w-100"}}
        >
            <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                    <MuiRFF.TextField
                        name="keyword"
                        label={t("keyword")}
                        fullWidth
                        variant="filled"
                        InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                        required={required.keyword}
                    />
                </Grid>
                <Grid xs={12} md={6}>
                    <MuiRFF.TextField
                        name="isPublic"
                        label={t("isPublic")}
                        select
                        fullWidth
                        variant="filled"
                        InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                        required={required.isPublic}
                    >
                        {isPublicOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </MuiRFF.TextField>
                </Grid>
                <Grid xs={12} sx={{display: "flex", justifyContent: "center", gap: 1}}>
                    <Button type="submit" variant="contained" color="primary" size="large" sx={{minWidth: 200}}>
                        {t("submit")}
                    </Button>
                </Grid>
            </Grid>
        </Form>
    );
};
